// Makao against the computer (owner 2026-10-27, .ai/makao.md "Owner decisions"): 4 players, one 52-card deck, 5 cards
// each, one plain card turned up starts the pile. Lay a card of the top's suit or rank (the queen on anything, anything
// on the queen); 2-4 cards of one rank in one turn. Cannot or will not: draw one, and lay it at once if it fits.
// 2 / 3: the next player draws 2 / 3; K♥: the next draws 5; K♠: the previous draws 5; an attack card matching the
// top by suit or rank passes it on and the counts add up. 4: the next player waits a turn (a 4 passes it on). Jack:
// asks for a rank 5-10 for one round, its player included. Ace: names the suit until someone lays that suit or an ace.
// The first player out wins; "Makao!" is said for the players (no penalty).
//
// Pure: no DOM, no React, no Math.random (the caller gives the rng). The cards and the suit mapping to the picture
// files are Kierki's (hearts/rules.ts: real suits here, `fileId` for the pictures).
// Seats: 0 is the player at the bottom, then clockwise 1 left, 2 top, 3 right.
import { shuffle } from '../engine/shuffle';
import { BASE_DECK, sameCard, sortHand, type HCard, type HSuit } from '../hearts/rules';

export { BASE_DECK, cardKey, fileId, sameCard, sortHand, type HCard, type HSuit } from '../hearts/rules';

export const SEATS = 4;
export const HAND_SIZE = 5;
/** The ranks a jack may ask for (the plain ones). */
export const ASKABLE_RANKS = [5, 6, 7, 8, 9, 10] as const;
export const SUITS: readonly HSuit[] = ['H', 'D', 'C', 'S'];

/** An open attack: `target` must answer it (an attack card for `draw`, a 4 for `wait`) or take it. */
export type Attack =
  /** Draw `count` cards; after taking them, play goes on from `resume`. */
  | { readonly kind: 'draw'; readonly count: number; readonly target: number; readonly resume: number }
  /** Sit out `count` turns (the current one included). */
  | { readonly kind: 'wait'; readonly count: number; readonly target: number };
/** An open demand: a jack's rank for `left` more turns, or an ace's suit until someone lays it. */
export type Demand = { readonly kind: 'rank'; readonly rank: number; readonly left: number } | { readonly kind: 'suit'; readonly suit: HSuit };

export interface MakaoState {
  readonly hands: readonly (readonly HCard[])[];
  /** The face-down stock; the last card is its top. */
  readonly stock: readonly HCard[];
  /** The face-up pile; the last card is its top. */
  readonly pile: readonly HCard[];
  readonly phase: 'play' | 'over';
  /** Whose turn it is (-1 when the deal is over). */
  readonly turn: number;
  /** The cards the seat on turn laid so far this turn (more of the same rank may follow, then `finishTurn`). */
  readonly laid: readonly HCard[];
  /** The card the seat on turn drew this turn; it may lay only this one (then `finishTurn`) or `pass`. */
  readonly drawn: HCard | null;
  readonly attack: Attack | null;
  readonly demand: Demand | null;
  /** Turns each seat still has to sit out (4s taken earlier). */
  readonly waits: readonly number[];
  /** The seat that went out first, else -1. */
  readonly winner: number;
  /** The shuffle pattern of this deal (52 numbers, engine/shuffle.ts). */
  readonly pattern: readonly number[];
}

export const next = (seat: number): number => (seat + 1) % SEATS;
export const prev = (seat: number): number => (seat + SEATS - 1) % SEATS;
export const top = (s: MakaoState): HCard => s.pile[s.pile.length - 1]!;

const isKingOfFight = (c: HCard): boolean => c.r === 13 && (c.s === 'H' || c.s === 'S');
/** Cards drawn by the next (K♠: the previous) player: 2, 3, K♥ and K♠ 5; else 0. */
export const attackValue = (c: HCard): number => (c.r === 2 || c.r === 3 ? c.r : isKingOfFight(c) ? 5 : 0);
/** A card with a power: 2, 3, 4, jack, queen, ace and the two fighting kings. */
export const isSpecial = (c: HCard): boolean => c.r <= 4 || c.r === 11 || c.r === 12 || c.r === 14 || isKingOfFight(c);
const without = (cards: readonly HCard[], out: HCard): HCard[] => {
  const i = cards.findIndex((c) => sameCard(c, out));
  return i < 0 ? cards.slice() : [...cards.slice(0, i), ...cards.slice(i + 1)];
};

/** A new deal (from `pattern`, else a new one from `rng`): 5 cards each, the first plain card turned up. */
export function startGame(pattern: readonly number[] | null, rng: () => number): MakaoState {
  const { order, pattern: used } = shuffle(BASE_DECK.length, pattern, rng);
  const cards = order.map((i) => BASE_DECK[i]!);
  const hands = [0, 1, 2, 3].map((p) => sortHand(cards.slice(p * HAND_SIZE, (p + 1) * HAND_SIZE)));
  // the stock's top is its last card: the cards after the hands, the first of them on top
  let stock = cards.slice(SEATS * HAND_SIZE).reverse();
  // turn up the first plain card; special ones go under the stock (there are 22 plain cards, one always comes)
  for (;;) {
    const c = stock[stock.length - 1]!;
    stock = stock.slice(0, -1);
    if (!isSpecial(c)) return { hands, stock, pile: [c], phase: 'play', turn: 0, laid: [], drawn: null, attack: null, demand: null, waits: [0, 0, 0, 0], winner: -1, pattern: used };
    stock = [c, ...stock];
  }
}

/** May `c` start a turn on this table (the first card laid)? */
export function fits(s: MakaoState, c: HCard): boolean {
  const t = top(s);
  if (s.attack?.kind === 'draw') return attackValue(c) > 0 && (c.s === t.s || c.r === t.r);
  if (s.attack?.kind === 'wait') return c.r === 4;
  if (s.demand?.kind === 'rank') return c.r === s.demand.rank || c.r === 11;
  if (s.demand?.kind === 'suit') return c.s === s.demand.suit || c.r === 14;
  return c.s === t.s || c.r === t.r || c.r === 12 || t.r === 12;
}

/** Must `seat` sit out this turn (a 4 taken earlier)? Then only `skipTurn`. */
export const mustWait = (s: MakaoState, seat: number): boolean => s.phase === 'play' && s.turn === seat && s.waits[seat]! > 0 && !s.laid.length;

/** The cards `seat` may lay now: the first card of a turn, one more of the rank laid, or the drawn card. */
export function legalCards(s: MakaoState, seat: number): HCard[] {
  if (s.phase !== 'play' || s.turn !== seat || mustWait(s, seat)) return [];
  const hand = s.hands[seat]!;
  if (s.laid.length) {
    const r = s.laid[0]!.r;
    // answering an attack, every card laid must be one (no plain king after a fighting one)
    return hand.filter((c) => c.r === r && (s.attack?.kind !== 'draw' || attackValue(c) > 0));
  }
  if (s.drawn) return fits(s, s.drawn) ? [s.drawn] : [];
  return hand.filter((c) => fits(s, c));
}

/** May the seat on turn still lay a card (more of the same rank) before `finishTurn`? */
export const canAdd = (s: MakaoState): boolean => s.laid.length > 0 && legalCards(s, s.turn).length > 0;
/** Does the turn's last card ask for a choice in `finishTurn` (a jack: a rank or none; an ace: a suit)? */
export const choiceNeeded = (s: MakaoState): 'rank' | 'suit' | null => {
  const last = s.laid[s.laid.length - 1];
  return !last ? null : last.r === 11 ? 'rank' : last.r === 14 ? 'suit' : null;
};

/** `seat` lays `card` (null: not allowed). Its last card ends the deal at once: it won. */
export function lay(s: MakaoState, seat: number, card: HCard): MakaoState | null {
  if (!legalCards(s, seat).some((c) => sameCard(c, card))) return null;
  const hands = s.hands.map((h, i) => (i === seat ? without(h, card) : h));
  const out: MakaoState = { ...s, hands, pile: [...s.pile, card], laid: [...s.laid, card], drawn: null };
  return hands[seat]!.length ? out : { ...out, phase: 'over', turn: -1, winner: seat, attack: null, demand: null };
}

/** The end of every turn: a jack's round counts down; the next seat is on (or `to`). */
function endTurn(s: MakaoState, to: number, demand: Demand | null = s.demand): MakaoState {
  const left = demand?.kind === 'rank' ? demand.left - 1 : 0;
  const d = demand?.kind === 'rank' ? (left > 0 ? { ...demand, left } : null) : demand;
  return { ...s, turn: to, laid: [], drawn: null, demand: d };
}

/** The seat on turn ends a turn it laid cards in: the cards' powers start (`choice`: the jack's rank, null for no
 *  demand; the ace's suit). Null when nothing was laid or the choice is missing or wrong. */
export function finishTurn(s: MakaoState, choice?: Choice): MakaoState | null {
  if (s.phase !== 'play' || !s.laid.length) return null;
  const seat = s.turn;
  const last = s.laid[s.laid.length - 1]!;
  const ask = choiceNeeded(s);
  if (ask === 'rank' && !(choice?.rank === null || (ASKABLE_RANKS as readonly number[]).includes(choice?.rank ?? 0))) return null;
  if (ask === 'suit' && !(choice?.suit && SUITS.includes(choice.suit))) return null;
  const draw = s.laid.reduce((n, c) => n + attackValue(c), 0);
  if (draw) {
    const before = s.attack?.kind === 'draw' ? s.attack.count : 0;
    // the direction comes from the turn's last attack card: K♠ hits the previous player, then play goes on after `seat`
    const back = [...s.laid].reverse().find((c) => attackValue(c) > 0)!;
    const target = back.r === 13 && back.s === 'S' ? prev(seat) : next(seat);
    const resume = target === prev(seat) ? next(seat) : next(target);
    return endTurn({ ...s, attack: { kind: 'draw', count: before + draw, target, resume } }, target, null);
  }
  if (last.r === 4) {
    const before = s.attack?.kind === 'wait' ? s.attack.count : 0;
    return endTurn({ ...s, attack: { kind: 'wait', count: before + s.laid.length, target: next(seat) } }, next(seat), null);
  }
  if (last.r === 11) {
    // a new jack replaces any demand; one full round: the 3 others and then the jack's player
    const demand: Demand | null = choice!.rank ? { kind: 'rank', rank: choice!.rank, left: SEATS + 1 } : null;
    return endTurn({ ...s, attack: null }, next(seat), demand);
  }
  if (last.r === 14) return endTurn({ ...s, attack: null }, next(seat), { kind: 'suit', suit: choice!.suit! });
  // a card of the asked suit meets the ace's demand
  return endTurn({ ...s, attack: null }, next(seat), s.demand?.kind === 'suit' ? null : s.demand);
}

/** Take `n` cards off the stock for `seat`; an empty stock gets the pile under its top card, shuffled by `rng`. */
function drawCards(s: MakaoState, seat: number, n: number, rng: () => number): { s: MakaoState; got: HCard[] } {
  let { stock, pile } = s;
  if (stock.length < n && pile.length > 1) {
    const under = pile.slice(0, -1);
    const { order } = shuffle(under.length, null, rng);
    stock = [...order.map((i) => under[i]!), ...stock];
    pile = pile.slice(-1);
  }
  const got = stock.slice(Math.max(0, stock.length - n)).reverse();
  stock = stock.slice(0, stock.length - got.length);
  const hands = s.hands.map((h, i) => (i === seat ? sortHand([...h, ...got]) : h));
  return { s: { ...s, stock, pile, hands }, got };
}

/** May `seat` draw now (its turn, nothing laid or drawn yet, not sitting out)? Under an attack this takes it. */
export const canDraw = (s: MakaoState, seat: number): boolean =>
  s.phase === 'play' && s.turn === seat && !s.laid.length && !s.drawn && !mustWait(s, seat);

/** `seat` draws: under a 2 / 3 / king it takes the whole count, under a 4 it sits out (no card), else one card, which
 *  it may lay at once if it fits (`drawn`; else the turn ends). Null when it may not draw now. */
export function drawCard(s: MakaoState, seat: number, rng: () => number): MakaoState | null {
  if (!canDraw(s, seat)) return null;
  if (s.attack?.kind === 'draw') {
    const { s: t } = drawCards(s, seat, s.attack.count, rng);
    return endTurn({ ...t, attack: null }, s.attack.resume);
  }
  if (s.attack?.kind === 'wait') {
    const waits = s.waits.map((w, i) => (i === seat ? w + s.attack!.count - 1 : w));
    return endTurn({ ...s, waits, attack: null }, next(seat));
  }
  const { s: t, got } = drawCards(s, seat, 1, rng);
  const card = got[0];
  if (!card) return endTurn(t, next(seat));
  const withCard = { ...t, drawn: card };
  return fits(withCard, card) ? withCard : endTurn(t, next(seat));
}

/** The seat that drew a card that fits keeps it and ends the turn. */
export function pass(s: MakaoState, seat: number): MakaoState | null {
  if (s.phase !== 'play' || s.turn !== seat || !s.drawn || s.laid.length) return null;
  return endTurn(s, next(seat));
}

/** A seat that has to sit out (`mustWait`) gives up this turn; an attack on it moves on to the next seat. */
export function skipTurn(s: MakaoState, seat: number): MakaoState | null {
  if (!mustWait(s, seat)) return null;
  const to = next(seat);
  const a = s.attack;
  const attack: Attack | null = !a ? null : a.kind === 'draw' ? { ...a, target: to, resume: next(to) } : { ...a, target: to };
  return endTurn({ ...s, attack, waits: s.waits.map((w, i) => (i === seat ? w - 1 : w)) }, to);
}

/** The score posted for the player's win: the cards left in the computer players' hands x 10 (0 if not won). */
export const winScore = (s: MakaoState): number => (s.winner === 0 ? s.hands.reduce((n, h, i) => (i === 0 ? n : n + h.length), 0) * 10 : 0);

// ---- the computer players (simple, fair: they see only their own hand, the table and the hand sizes) -------------

/** A jack's rank (null: ask nothing) or an ace's suit, given with `finishTurn`. */
export interface Choice {
  readonly rank?: number | null;
  readonly suit?: HSuit;
}
export type BotAction =
  | { readonly type: 'lay'; readonly card: HCard }
  | { readonly type: 'finish'; readonly choice: Choice | undefined }
  | { readonly type: 'draw' }
  | { readonly type: 'pass' }
  | { readonly type: 'skip' };

/** How eager a computer player is to lay a card first: plain cards first (the rank it holds most of), attacks when
 *  the next player is close to going out, jacks, aces and queens kept for later. */
function eagerness(s: MakaoState, seat: number, c: HCard): number {
  const hand = s.hands[seat]!;
  const same = hand.filter((x) => x.r === c.r).length;
  const victim = s.hands[c.r === 13 && c.s === 'S' ? prev(seat) : next(seat)]!.length;
  if (attackValue(c) || c.r === 4) return victim <= 2 ? 500 + same : 200 + same;
  if (c.r === 11) return 120 + same;
  if (c.r === 14) return 110 + same;
  if (c.r === 12) return 100 + same;
  return 300 + same * 10 + c.r / 100;
}

/** The suit or rank a computer player asks for: the one it holds most of (a jack with no plain card asks nothing). */
function botChoice(s: MakaoState, seat: number): Choice | undefined {
  const hand = s.hands[seat]!;
  const ask = choiceNeeded(s);
  if (ask === 'rank') {
    let best: number | null = null;
    let most = 0;
    for (const r of ASKABLE_RANKS) {
      const n = hand.filter((c) => c.r === r).length;
      if (n > most) [best, most] = [r, n];
    }
    return { rank: best };
  }
  if (ask === 'suit') {
    const count = (suit: HSuit) => hand.filter((c) => c.s === suit && c.r !== 14).length;
    return { suit: SUITS.reduce((m, x) => (count(x) > count(m) ? x : m), top(s).s) };
  }
  return undefined;
}

/** The next thing a computer player does on its turn (always allowed). */
export function botAction(s: MakaoState, seat: number): BotAction {
  if (mustWait(s, seat)) return { type: 'skip' };
  const legal = legalCards(s, seat);
  if (s.laid.length) {
    // more of the same rank: the one whose suit it holds least of first, so the last one leaves its best suit on top
    if (!legal.length) return { type: 'finish', choice: botChoice(s, seat) };
    const hand = s.hands[seat]!;
    const count = (suit: HSuit) => hand.filter((c) => c.s === suit).length;
    return { type: 'lay', card: legal.reduce((m, c) => (count(c.s) < count(m.s) ? c : m)) };
  }
  if (s.drawn) return legal.length ? { type: 'lay', card: s.drawn } : { type: 'pass' };
  if (!legal.length) return { type: 'draw' };
  return { type: 'lay', card: legal.reduce((m, c) => (eagerness(s, seat, c) > eagerness(s, seat, m) ? c : m)) };
}

/** Applies a computer player's (or any) action; null when it is not allowed. */
export function applyAction(s: MakaoState, seat: number, a: BotAction, rng: () => number): MakaoState | null {
  switch (a.type) {
    case 'lay': return lay(s, seat, a.card);
    case 'finish': return s.turn === seat ? finishTurn(s, a.choice) : null;
    case 'draw': return drawCard(s, seat, rng);
    case 'pass': return pass(s, seat);
    case 'skip': return skipTurn(s, seat);
  }
}
